"use client";

import { useState, useMemo, useCallback, useEffect } from "react";
import { ArrowRightLeft, Building, Search, Check, AlertTriangle, GitMerge, MoveRight } from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { useToast } from "@/app/_module/hooks/use-toast";

import { useGetSubAgencies } from "../../hooks/use-hook";
import type { SubAgency } from "../../types/types";
import {
  createTransferJob,
  type AgencyTransferMode,
} from "../../services/transfer-agencies-services";
import { useTransferProgress } from "./TransferProgressContext";
import { isDemoMode } from "@/app/_module/utils/demoMode";

interface TransferAgenciesModalProps {
  /** The agency context whose subtree we list as source/target candidates. */
  rootAgencyId: string;
  rootAgencyName: string;
  trigger: React.ReactNode;
  /** Optionally pre-check source agencies (e.g. from sub-agency checkboxes). */
  preselectedSourceIds?: string[];
}

interface AgencyOption {
  id: string;
  name: string;
  parentAgencyId: string;
  depth: number;
}

export const TransferAgenciesModal = function ({
  rootAgencyId,
  rootAgencyName,
  trigger,
  preselectedSourceIds,
}: TransferAgenciesModalProps) {
  const [open, setOpen] = useState(false);
  const [sourceIds, setSourceIds] = useState<Set<string>>(
    () => new Set(preselectedSourceIds ?? []),
  );

  // Sync the pre-checked sources whenever the modal opens (the table's
  // checkbox selection is captured at open time).
  useEffect(() => {
    if (open) setSourceIds(new Set(preselectedSourceIds ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [mode, setMode] = useState<AgencyTransferMode>("reparent");
  const [mergeConfirmed, setMergeConfirmed] = useState(false);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { startJob } = useTransferProgress();

  const { data: subAgenciesData, isLoading } = useGetSubAgencies({
    parentAgencyId: rootAgencyId,
    page: 1,
    limit: 200,
  });

  const normalize = (input: unknown): SubAgency[] => {
    if (Array.isArray(input)) return input.filter(Boolean) as SubAgency[];
    if (input && typeof input === "object") {
      const obj = input as Record<string, unknown>;
      if (Array.isArray(obj.items)) return obj.items.filter(Boolean) as SubAgency[];
      if (obj.data && typeof obj.data === "object") {
        const d = obj.data as Record<string, unknown>;
        if (Array.isArray(d.items)) return d.items.filter(Boolean) as SubAgency[];
      }
    }
    return [];
  };

  // Flatten the subtree (depth-first) for selection + descendant lookups.
  const { options, descendantsOf } = useMemo(() => {
    const all = normalize(subAgenciesData).filter((a) => a.isActive);
    const childrenMap = new Map<string, SubAgency[]>();
    for (const a of all) {
      const p = a.parentAgencyId;
      if (!childrenMap.has(p)) childrenMap.set(p, []);
      childrenMap.get(p)!.push(a);
    }
    const flat: AgencyOption[] = [];
    const traverse = (parentId: string, depth: number) => {
      for (const child of childrenMap.get(parentId) || []) {
        flat.push({ id: child.id, name: child.name, parentAgencyId: child.parentAgencyId, depth });
        traverse(child.id, depth + 1);
      }
    };
    traverse(rootAgencyId, 0);

    // For each agency, the set of its descendant ids (so a target can't be a
    // selected source's descendant — that would be a cycle).
    const descMap = new Map<string, Set<string>>();
    const collect = (id: string): Set<string> => {
      if (descMap.has(id)) return descMap.get(id)!;
      const set = new Set<string>();
      for (const child of childrenMap.get(id) || []) {
        set.add(child.id);
        for (const d of collect(child.id)) set.add(d);
      }
      descMap.set(id, set);
      return set;
    };
    flat.forEach((o) => collect(o.id));

    return { options: flat, descendantsOf: descMap };
  }, [subAgenciesData, rootAgencyId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, search]);

  // Target candidates: the root + any agency that isn't a selected source or a
  // descendant of one (prevents loops client-side; backend also enforces).
  const blockedTargets = useMemo(() => {
    const blocked = new Set<string>(sourceIds);
    for (const sid of sourceIds) {
      for (const d of descendantsOf.get(sid) || []) blocked.add(d);
    }
    return blocked;
  }, [sourceIds, descendantsOf]);

  const targetOptions: AgencyOption[] = useMemo(() => {
    const root: AgencyOption = { id: rootAgencyId, name: `${rootAgencyName} (top)`, parentAgencyId: "", depth: 0 };
    return [root, ...options].filter((o) => !blockedTargets.has(o.id));
  }, [options, rootAgencyId, rootAgencyName, blockedTargets]);

  const toggleSource = useCallback((id: string) => {
    setSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // If the target became blocked by this selection, clear it.
    setTargetId((t) => (t && t === id ? null : t));
  }, []);

  const reset = () => {
    setSourceIds(new Set(preselectedSourceIds ?? []));
    setTargetId(null);
    setMode("reparent");
    setMergeConfirmed(false);
    setSearch("");
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const canSubmit =
    sourceIds.size > 0 &&
    !!targetId &&
    // Block not just target===source but any target that's a selected source OR a
    // descendant of one (a cycle). Previously only `sourceIds.has(targetId)` was
    // checked, so a target chosen before selecting its ancestor stayed submittable.
    !blockedTargets.has(targetId) &&
    (mode === "reparent" || mergeConfirmed) &&
    !submitting;

  const handleSubmit = async () => {
    // DEMO SANDBOX: agency structure changes are real, cross-account and hard to
    // undo — a visitor moving agents or spinning up sub-agencies would reshape a
    // workspace the next visitor inherits. Offer the trial instead.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Transferring agencies is disabled in the demo sandbox. Start a free trial to restructure your real agency.",
        }),
      );
      return;
    }


    if (!targetId || sourceIds.size === 0) return;
    setSubmitting(true);
    try {
      const res = await createTransferJob({
        mode,
        targetAgencyId: targetId,
        sourceAgencyIds: Array.from(sourceIds),
      });
      const jobId = (res as any)?.jobId || (res as any)?.data?.jobId;
      if (!jobId) throw new Error("No job id returned");
      startJob(jobId, mode, sourceIds.size);
      toast({
        title: "Transfer started",
        description: `${sourceIds.size} agenc${sourceIds.size === 1 ? "y" : "ies"} ${mode === "merge" ? "merging into" : "moving under"} the target.`,
      });
      handleClose();
    } catch (err: any) {
      toast({
        title: "Could not start transfer",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div onClick={() => setOpen(true)}>{trigger}</div>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : handleClose())}>
        <DialogContent className="sm:max-w-lg z-[70]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-primary" />
              Transfer Agencies
            </DialogTitle>
            <DialogDescription>
              Move whole agencies (their agents, leads, and history) into another agency you own.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* Mode toggle */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("reparent")}
                className={`flex items-start gap-2 rounded-md border p-2.5 text-left text-sm transition-colors ${
                  mode === "reparent" ? "border-primary bg-primary/5" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <MoveRight className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                <span>
                  <span className="font-medium block">Re-parent</span>
                  <span className="text-xs text-muted-foreground">Keep each agency intact, under the target. Instant, reversible.</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setMode("merge")}
                className={`flex items-start gap-2 rounded-md border p-2.5 text-left text-sm transition-colors ${
                  mode === "merge" ? "border-amber-400 bg-amber-50" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <GitMerge className="h-4 w-4 mt-0.5 text-amber-600 flex-shrink-0" />
                <span>
                  <span className="font-medium block">Merge</span>
                  <span className="text-xs text-muted-foreground">Dissolve into the target. Agents + leads absorbed.</span>
                </span>
              </button>
            </div>

            {/* Source multi-select */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Agencies to transfer ({sourceIds.size} selected)</p>
              <div className="relative mb-1.5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <input
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Search agencies..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="max-h-44 overflow-y-auto border rounded-md">
                {isLoading ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
                ) : filtered.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">No agencies found</div>
                ) : (
                  filtered.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 border-b last:border-b-0 ${
                        sourceIds.has(o.id) ? "bg-primary/5" : ""
                      }`}
                      style={{ paddingLeft: `${12 + o.depth * 16}px` }}
                      onClick={() => toggleSource(o.id)}
                    >
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded border flex-shrink-0 ${
                          sourceIds.has(o.id) ? "bg-primary border-primary" : "border-gray-300"
                        }`}
                      >
                        {sourceIds.has(o.id) && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <Building className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{o.name}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Target picker */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Transfer into</p>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                value={targetId ?? ""}
                onChange={(e) => setTargetId(e.target.value || null)}
              >
                <option value="">Select a target agency…</option>
                {targetOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {" ".repeat(o.depth * 2)}
                    {o.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Merge destructive confirm */}
            {mode === "merge" && sourceIds.size > 0 && (
              <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 flex-shrink-0"
                  checked={mergeConfirmed}
                  onChange={(e) => setMergeConfirmed(e.target.checked)}
                />
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
                <span className="leading-snug">
                  I understand this <strong>dissolves</strong> the selected agenc{sourceIds.size === 1 ? "y" : "ies"}; their agents and leads move into the target and their sub-agencies are re-parented to it. <strong>Subscription seats, dial credits, and commissions do NOT move</strong> and stay with the retired agenc{sourceIds.size === 1 ? "y" : "ies"} — review billing after. This can&apos;t be easily undone.
                </span>
              </label>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? "Starting…" : mode === "merge" ? "Merge agencies" : "Transfer agencies"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TransferAgenciesModal;
